import React, { useEffect, useRef, useState } from 'react'
import { Trans } from 'next-i18next'
import { Box, Text, TextLink } from '@machobear/design-system'
import { TurnstileInstance } from '@marsidev/react-turnstile'
import { Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik'
import { useOpenInWindow } from 'use-open-window'
import { Userpilot } from 'userpilot'

import config from '@/config'
import ENV_VARS from '@/config'
import {
  registerLandlord,
  registerRenter,
  registerSocial
} from '@/models/auth/ports'
import { usePublicSession } from '@/models/session/hooks'
import { useDispatch } from '@/lib/redux'
import Yup from '@/lib/yup'
import handleSession from '@/utils/handle-session'
import { isPhoneNumber } from '@/utils/validation'
import usePasswordRequirements from '@/hooks/use-password-requirements'
import useSessionComplete from '@/hooks/use-session-complete'
import { PasswordRequirements, SocialButtons } from '@/features/Auth/components'
import {
  formatUserRegistrationValues,
  getInitialFullName,
  getSubmitEventId,
  isSocialRegistration
} from '@/features/Auth/utils'
import { Button } from '@/components/button-new'
import { Captcha } from '@/components/captcha/captcha'
import {
  Error,
  FieldDropdown,
  FieldInput,
  Group,
  PasswordInput,
  Row
} from '@/components/form'
import { toast } from '@/components/toast-notifications'

import { useDSLogin } from '../Context/ds-login-provider'
import { useLogin } from './context'
import * as styles from './styles.css'
import { AccountType, DSLoginStepProps, FormProps, Provider } from './types'

export type DSRegisterProps = Pick<
  DSLoginStepProps,
  'setCurrentStep' | 'title' | 'data' | 'listingId' | 't' | 'onClose'
> & {
  defaultOperationType?: AccountType
  autoFocusName?: boolean
}

const { DASHBOARD_URL } = ENV_VARS
const RegisterForDS: React.FC<DSRegisterProps> = ({
  setCurrentStep,
  data = {},
  listingId,
  t,
  onClose,
  defaultOperationType = undefined,
  autoFocusName
}) => {
  const { username, social, setSocial, setExistingSocialState, eventId } =
    useLogin()
  const dispatch = useDispatch()
  const [usernameClicked, setUsernameClicked] = useState(false)
  const captchaRef = useRef<TurnstileInstance | null>(null)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const isLoggedIn = useSessionComplete()
  const [showPassword, setShowPassword] = useState(false)
  const { requirements, checkRequirements } = usePasswordRequirements()
  const publicChatSession = usePublicSession()
  const isSocial = isSocialRegistration(social)
  const { afterLogin, afterLoginRef } = useDSLogin()
  const socialPlatform = social?.platform
  const signupMethod = isSocial ? socialPlatform : 'email'
  const location = window?.location
  const redirectPath = new URLSearchParams(location.search).get('redirect')

  const [selectedPlatform, setSelectedPlatform] = useState<
    Provider | undefined
  >()
  const options = {
    url: '',
    centered: true,
    specs: {
      width: 800,
      height: 600
    }
  }
  const [handleWindowOpen, newWindowHandle] = useOpenInWindow(options)

  useEffect(() => {
    const messageListener = async (event: MessageEvent) => {
      // Handle log in by social platform
      if (event.data.source_message === 'mb_social_login') {
        setSocial({ platform: selectedPlatform, data: event.data })
        if (!newWindowHandle) return
        newWindowHandle.close()
        switch (event.data.message_key) {
          case 'msg.login_with_social_login_unauthorized':
          case 'msg.login_with_social_login_unauthorized_by_provider':
            setExistingSocialState(true)
            setCurrentStep('existingSocialUser')
            break
          case 'err.unauthorized_token':
          case 'err.email_was_not_sent_by_provider':
            toast(event?.data?.message, {
              type: 'error',
              title: t(
                'b.auth.view.login_password.failed.title',
                'Login Failed'
              )
            })
            return
          default:
            if (event.data.new === 0) {
              await handleSession(dispatch.session.loadSession())
              return onClose()
            }
        }
      }
    }
    window.addEventListener('message', messageListener)

    return () => {
      window.removeEventListener('message', messageListener)
    }
  }, [
    setExistingSocialState,
    newWindowHandle,
    setCurrentStep,
    setSocial,
    selectedPlatform,
    dispatch,
    t,
    onClose
  ])

  const afterLandlordRegister = () => {
    afterLoginRef.current
      ? afterLogin()
      : (window.location.href = redirectPath
          ? `${DASHBOARD_URL}${redirectPath}`
          : `${DASHBOARD_URL}/listings`)
  }

  const afterRenterRegister = () => {
    if (afterLoginRef.current) {
      return afterLogin()
    } else if (redirectPath) {
      window.location.href = `${DASHBOARD_URL}${redirectPath}`
    } else if (typeof listingId !== 'undefined') {
      window.location.href = `${DASHBOARD_URL}/application/create/${listingId}`
    } else {
      window.location.href = `${DASHBOARD_URL}/profile`
    }
  }

  const formEmail = !isPhoneNumber(username) ? username : ''
  const initialUserName =
    data?.username ||
    social?.data?.user_profile?.email ||
    publicChatSession?.username ||
    formEmail ||
    ''

  const initialValues = {
    username: initialUserName,
    full_name: getInitialFullName(data, social, publicChatSession),
    password: '',
    account_type: defaultOperationType,
    social_login: isSocial ? 'true' : 'false'
  }
  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .email()
      .required(
        t('b.auth.edit.email_required.error', 'Please enter your email')
      ),
    full_name: Yup.string()
      .matches(
        /^[ A-Za-zÀ-Ÿ-]*$/,
        t(
          'b.profile.name.special_characters.error',
          'Cannot include special characters'
        )
      )
      .matches(
        /^[\w-]+(?:\s[\w-]+)+\s*$/,
        t('b.auth.edit.full_name.error', 'Please enter your full name')
      )
      .required(
        t('b.auth.edit.full_name.error', 'Please enter your full name')
      ),
    password: Yup.string().when('social_login', {
      is: 'false',
      then: Yup.string()
        .min(8)
        .matches(
          /^\S*$/,
          t(
            'b.auth.edit.password_whitespace.error',
            'Whitespace characters not allowed'
          )
        )
        .matches(
          /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^{}()[\]./\\,>~+<'"`|\-=_;:&*?])[A-Za-z\d!@#$%^{}()[\]./\\,>~+<'"`|\-=_;:&*?]+$/,
          t(
            'b.auth.edit.password_requirements.error',
            "Password doesn't match the requirements"
          )
        )
        .required(
          t('b.auth.edit.password_required.error', 'Please enter a password')
        )
    }),
    account_type: Yup.string().required(
      t('b.auth.edit.account_type_required.error', 'Please select account type')
    )
  })
  const onSubmit = async (
    values: FormProps,
    { setSubmitting }: FormikHelpers<FormProps>
  ) => {
    const isLandlord = values.account_type === 'landlord'
    const formattedValues = formatUserRegistrationValues(values, social)

    const loginHandler = async (
      response: { ok?: boolean },
      body: { message?: string; message_key?: string }
    ) => {
      await handleSession(dispatch.session.loadSession())
      setSubmitting(false)

      if (!response.ok) {
        if (
          body.message_key ===
          'phone_number_or_email_already_exists_in_the_system'
        ) {
          setCurrentStep('existingEmail')
          return
        }
        toast(body?.message, {
          title: t(
            'b.auth.create.registration_failed.text',
            'Registration Failed'
          ),
          type: 'error'
        })
        return
      }

      window.dataLayer.push({
        event: getSubmitEventId(eventId, signupMethod, values.account_type),
        userEmail: formattedValues.email,
        userFirstName: formattedValues.first_name,
        userLastName: formattedValues.last_name
      })

      onClose()

      if (isLandlord) {
        afterLandlordRegister()
        Userpilot.track('goal_landlord_signup_essentials')
      } else {
        afterRenterRegister()
      }
    }

    if (isSocial) {
      const beSocialPlatform =
        social.platform === 'fb' ? 'facebook' : social.platform
      const { response, body } = await registerSocial(
        {
          body: formattedValues
        },
        { platform: beSocialPlatform }
      )
      loginHandler(response, body)
    } else {
      const registerPort = isLandlord ? registerLandlord : registerRenter
      const { response, body } = await registerPort(
        {
          body: formattedValues
        },
        undefined,
        undefined,
        {
          headers: {
            'X-Captcha-Token': values.captcha
          }
        }
      )
      loginHandler(response, body)
    }
  }

  const shouldValidateCaptcha =
    !isLoggedIn && config.ENABLE_REGISTER_FORM_CAPTCHA

  return (
    <Formik
      validate={values => {
        const errors: FormikErrors<FormProps> = {}
        if (shouldValidateCaptcha && !values.captcha) {
          errors.captcha = t(
            `Captcha is required. Please wait while it's validating.`
          )
        }

        return errors
      }}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        isSubmitting,
        isValid,
        setFieldValue,
        setFieldTouched,
        setFieldError,
        dirty
      }: FormikProps<FormProps>) => (
        <Box component={Form} noValidate width="full">
          <Row>
            <Group col={12}>
              <FieldInput
                name="full_name"
                autoComplete="section-register given-name"
                placeholder={t('b.auth.create.full_name.label', 'Full name')}
                //todo: after deploy find source of issue from auto focus breaking in some contexts
                autoFocus={autoFocusName}
                required
              />
            </Group>
          </Row>
          <Row>
            <Group col={12}>
              <FieldInput
                name="username"
                type="email"
                autoComplete="section-register username email"
                placeholder={t('common.email', 'Email')}
                required
                hideError={!usernameClicked}
                onClick={() => setUsernameClicked(true)}
              />
            </Group>
          </Row>
          {!isSocial && (
            <>
              <Row>
                <Group col={12}>
                  <PasswordInput
                    name="password"
                    autoComplete="section-register new-password"
                    placeholder={t(
                      'b.auth.view.password.placeholder',
                      'Password'
                    )}
                    required
                    onChange={e => {
                      setFieldValue('password', e.target.value)
                      checkRequirements(e.target.value)
                    }}
                    visible={showPassword}
                    setVisible={setShowPassword}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    hideError={passwordFocused}
                  />
                </Group>
              </Row>
              <PasswordRequirements
                requirements={requirements}
                show={passwordFocused}
              />
            </>
          )}
          <Row>
            <Group col={12}>
              <FieldDropdown
                name="account_type"
                placeholder={t(
                  'b.auth.edit.account_type.placeholder',
                  'Select account type'
                )}
                required
                options={[
                  {
                    value: 'tenant',
                    label: t('b.auth.edit.im_renter.text', `I'm a Renter`)
                  },
                  {
                    value: 'landlord',
                    label: t('b.auth.edit.im_landlord.text', `I'm a Landlord`)
                  }
                ]}
              />
            </Group>
          </Row>
          {shouldValidateCaptcha && (
            <Row>
              <Group col={12}>
                <Captcha
                  id="register-form"
                  ref={captchaRef}
                  onSuccess={captcha => setFieldValue('captcha', captcha)}
                  onExpire={() => setFieldValue('captcha', '')}
                  onError={() => {
                    setFieldValue('captcha', '', false)
                    setFieldTouched('captcha', true, false)
                    setFieldError(
                      'captcha',
                      t('Captcha validation failed. Please try again.')
                    )
                  }}
                  options={{ action: 'register' }}
                />
                <Error name="captcha" />
              </Group>
            </Row>
          )}
          <Row>
            <Group col={12}>
              <Text size="xsmall" tone="tertiary">
                <Trans t={t} i18nKey="b.auth.edit.signup_terms.text">
                  By clicking “Sign Up”, I confirm that I have read and agree to
                  liv.rent{`'`}s{' '}
                  <TextLink
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </TextLink>{' '}
                  and{' '}
                  <TextLink
                    href="privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </TextLink>
                  .
                </Trans>
              </Text>
            </Group>
          </Row>
          <Button
            id={getSubmitEventId(eventId, signupMethod, values.account_type)}
            theme="primary"
            type="submit"
            width="100%"
            disabled={isSubmitting || !isValid || !dirty}
          >
            {t('common.sign_up', 'Sign Up')}
          </Button>
          {!isSocial && (
            <>
              <Box display="flex" justifyContent="center" width="full">
                <Box
                  className={styles.orTextStyle}
                  width="full"
                  marginY="gutter"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {t('b.auth.view.or_login_with.text', 'OR LOG IN WITH')}
                </Box>
              </Box>
              <SocialButtons
                onClick={(item, socialItem, event) => {
                  handleWindowOpen(event, {
                    url: socialItem.url
                  })
                  setSelectedPlatform(item)
                }}
                trackingId={eventId}
              />
            </>
          )}
        </Box>
      )}
    </Formik>
  )
}

export default RegisterForDS
